/**
 * Lexical-based rich-text editor (composable + editable).
 *
 * Despite the name, this is NOT a markdown parser. The MarkdownShortcutPlugin
 * transforms typed markdown shortcuts (e.g. `#` then space -> H1) at the moment of typing,
 * but the editor state stored/loaded is always Lexical JSON. For programmatic
 * ingestion of markdown / HTML / plain text from non-typing sources (AI agents,
 * migrations), use the server-side converter at `pollux-api/lib/contentConverter.js`.
 * Name kept for back-compat.
 */
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { TRANSFORMERS } from "@lexical/markdown";
import { MentionNode } from '../plugins/MentionsPlugin';
import { ImageNode } from '../nodes/ImageNode';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { $getRoot, $createParagraphNode, $isElementNode } from 'lexical';
import { $generateNodesFromDOM } from '@lexical/html';

import { buildLexicalFromPlainText, isLexicalJson } from '../utils/lexicalContent';
import ToolbarPlugin from "../plugins/ToolbarPlugin";
import AutoLinkPlugin from "../plugins/AutoLinkPlugin";
import ImagesPlugin from "../plugins/ImagesPlugin";
import MentionsPlugin from "../plugins/MentionsPlugin";
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';

/** True if the string looks like HTML markup. */
function looksLikeHtml(str) {
    return typeof str === 'string' && /<\/?[a-z][a-z0-9]*\b[^>]*>/i.test(str);
}

/** Build an editorState initializer that hydrates the editor from an HTML string. */
function htmlInitializer(html) {
    return (editor) => {
        const root = $getRoot();
        root.clear();
        try {
            const parser = new DOMParser();
            const dom = parser.parseFromString(html, 'text/html');
            const nodes = $generateNodesFromDOM(editor, dom);
            // Lexical's root only accepts ElementNodes/DecoratorNodes as children.
            // Group inline nodes (e.g. raw TextNodes) into paragraphs.
            let pendingParagraph = null;
            for (const node of nodes) {
                if ($isElementNode(node)) {
                    if (pendingParagraph) { root.append(pendingParagraph); pendingParagraph = null; }
                    root.append(node);
                } else {
                    if (!pendingParagraph) pendingParagraph = $createParagraphNode();
                    pendingParagraph.append(node);
                }
            }
            if (pendingParagraph) root.append(pendingParagraph);
        } catch (err) {
            console.warn('MarkdownEditor: failed to parse HTML, falling back to empty document', err);
        }
        if (root.getFirstChild() === null) {
            root.append($createParagraphNode());
        }
    };
}

// allowHtml is off for a caller whose text is never markup: a bug report quoting <div class="cart-total"> looks like HTML, and parsing it swallows the tags the reporter meant to
// show.
function sanitizeEditorContent(content, allowHtml) {
    if (!content) return null;
    if (typeof content === 'string') {
        if (isLexicalJson(content)) return content;
        if (allowHtml && looksLikeHtml(content)) return htmlInitializer(content);
        return buildLexicalFromPlainText(content);
    }
    return content;
}

const EditorContainer = styled(Box)(({ theme, disabled }) => ({
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    opacity: disabled ? 0.7 : 1,
    pointerEvents: disabled ? "none" : "auto",
    '& table': {
        borderCollapse: 'collapse',
        margin: theme.spacing(1, 0),
        width: 'auto',
    },
    '& th, & td': {
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0.5, 1),
        textAlign: 'left',
        verticalAlign: 'top',
    },
    '& th': {
        backgroundColor: theme.palette.action.hover,
        fontWeight: theme.typography.fontWeightBold,
    },
    '& th p, & td p': {
        margin: 0,
    },
    '& code': {
        fontFamily: 'monospace',
        fontSize: theme.typography.size.md,
        backgroundColor: theme.palette.action.hover,
        padding: theme.spacing(0.25, 0.5),
        borderRadius: theme.shape.borderRadius / 2,
    },
    '& blockquote': {
        borderLeft: `4px solid ${theme.palette.divider}`,
        paddingLeft: theme.spacing(1.5),
        margin: theme.spacing(1, 0),
        color: theme.palette.text.secondary,
    },
    '& hr': {
        border: 'none',
        borderTop: `1px solid ${theme.palette.divider}`,
        margin: theme.spacing(2, 0),
    },
    // A nested list lives inside its own list item; that wrapper must not draw a marker of its own
    '& .rt-nested-listitem': {
        listStyle: 'none',
    },
    '& .rt-nested-listitem::before, & .rt-nested-listitem::after': {
        display: 'none',
    },
    '& ul ul, & ul ol, & ol ul, & ol ol': {
        margin: 0,
    },
    '& ul': { listStyleType: 'disc' },
    '& ul ul': { listStyleType: 'circle' },
    '& ul ul ul': { listStyleType: 'square' },
    '& .rt-strikethrough': { textDecoration: 'line-through' },
    '& .rt-underline': { textDecoration: 'underline' },
    '& .rt-underline-strikethrough': { textDecoration: 'underline line-through' },
}));

const ContentEditableStyled = styled(ContentEditable)(({ theme }) => ({
    minHeight: "150px",
    padding: theme.spacing(2),
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    color: theme.palette.text.primary,
}));

const Placeholder = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(0.5, 1),
    color: theme.palette.text.secondary,
    pointerEvents: "none",
}));

const MarkdownEditor = React.memo(({
    initialContent = null,
    onChange,
    disabled = false,
    placeholder = null,
    autoFocus = true, // New prop with default value true
    ignoreSelectionChange = true,
    allowHtml = true,
    editorRef = null,
    ...rest
}) => {
    // Dev-time warning for the deprecated `value` prop. Lexical is uncontrolled; callers must
    // pass `initialContent` (one-time seed) instead. Submission still works via `onChange`.
    if (process.env.NODE_ENV !== 'production' && 'value' in rest) {
        // eslint-disable-next-line no-console
        console.warn('[MarkdownEditor] `value` prop is not supported — use `initialContent`. The Lexical editor is uncontrolled.');
    }

    const safeContent = useMemo(() => sanitizeEditorContent(initialContent, allowHtml), [initialContent, allowHtml]);

    const initialConfig = useMemo(() => ({
        namespace: "MarkdownEditor",
        theme: {
            text: {
                strikethrough: 'rt-strikethrough',
                underline: 'rt-underline',
                underlineStrikethrough: 'rt-underline-strikethrough',
            },
            list: {
                nested: {
                    listitem: 'rt-nested-listitem',
                },
            },
        },
        onError: (error) => {
            console.warn("MarkdownEditor:", error?.message || error);
        },
        nodes: [
            HeadingNode,
            QuoteNode,
            ListItemNode,
            ListNode,
            CodeHighlightNode,
            CodeNode,
            TableCellNode,
            TableNode,
            TableRowNode,
            AutoLinkNode,
            LinkNode,
            MentionNode,
            ImageNode,
            HorizontalRuleNode,
        ],
        editorState: safeContent || (() => {
            const root = $getRoot();
            if (root.getFirstChild() === null) {
                const paragraph = $createParagraphNode();
                root.append(paragraph);
            }
        }),
    }), [safeContent]);

    const debounceRef = useRef(null);
    useEffect(() => () => clearTimeout(debounceRef.current), []);

    const handleEditorChange = useCallback((editorState) => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            editorState.read(() => {
                const json = JSON.stringify(editorState);
                if (typeof onChange === 'function') {
                    onChange(json);
                }
            });
        }, 150);
    }, [onChange]);

    return (
        <EditorContainer disabled={disabled}>
            <LexicalComposer initialConfig={initialConfig}>
                <ToolbarPlugin />
                <RichTextPlugin
                    contentEditable={<ContentEditableStyled />}
                    placeholder={placeholder ? <Placeholder>{placeholder}</Placeholder> : null}
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <HistoryPlugin />
                {autoFocus && <AutoFocusPlugin />} {/* Conditionally render AutoFocusPlugin */}
                <LinkPlugin />
                <AutoLinkPlugin />
                <ListPlugin />
                <HorizontalRulePlugin />
                <MentionsPlugin />
                <ImagesPlugin />
                <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
                <TabIndentationPlugin />
                <OnChangePlugin onChange={handleEditorChange} ignoreSelectionChange={ignoreSelectionChange} />
                {editorRef && <EditorRefPlugin editorRef={editorRef} />}
            </LexicalComposer>
        </EditorContainer>
    );
});

export default MarkdownEditor;